import React, { useState } from "react";
import { T } from "../design/tokens.js";
import { generateTrainingPlan, PLAN_TEMPLATES, TAPER_DEPTHS, PHASE_NAMES, PHASE_COLORS, PHASE_BG, raceTypeFromDistance } from "../lib/trail.js";
import { parseLocalDate } from "../lib/utils.js";

/* ═══════════════════════════════════════════════════════════════════════════
   CoachPage — Plan d'entraînement adaptatif + Catalogue de séances
   Extrait de App.js (TabCoach + TrainingPlanTab) et unifié ici.
═══════════════════════════════════════════════════════════════════════════ */

// ── Primitives locales (copies simplifiées des primitives App.js) ──────────
const Card = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{
    background: T.surface, borderRadius: 12, padding: 20,
    boxShadow: T.shadow, border: `0.5px solid ${T.border}`,
    ...style,
  }}>
    {children}
  </div>
);

const Btn = ({ children, onClick, variant, style }) => {
  const bg = variant === "lime" ? T.gold : T.navy;
  return (
    <button onClick={onClick} style={{
      background: bg, color: "#fff", border: "none",
      padding: "10px 20px", borderRadius: 8, fontWeight: 700, fontSize: 13,
      cursor: "pointer", fontFamily: T.fontUI,
      ...style,
    }}>
      {children}
    </button>
  );
};

// ── Helpers locaux TRIMP / ACWR / PMC ────────────────────────────────────
// (calcACWR et calcPMC ne sont pas exportés depuis App.js)
const _calcTrimp = (dureeMin, fc_moy, fc_repos = 52, fc_max = 192) => {
  if (!fc_repos || !fc_max || !fc_moy) return Math.round(dureeMin * 0.7);
  const hrr = (fc_moy - fc_repos) / (fc_max - fc_repos);
  return Math.round(dureeMin * hrr * (0.64 * Math.exp(1.92 * hrr)));
};

const calcACWR = (activities) => {
  const now = new Date();
  const getTrimp = (r) => r.trimp || _calcTrimp(r.duree, r.fc_moy);
  const acute = activities
    .filter(r => { const d = (now - parseLocalDate(r.date)) / 86400000; return d >= 0 && d <= 7; })
    .reduce((s, r) => s + getTrimp(r), 0);
  const chronic4w = activities
    .filter(r => { const d = (now - parseLocalDate(r.date)) / 86400000; return d >= 0 && d <= 28; })
    .reduce((s, r) => s + getTrimp(r), 0) / 4;
  return chronic4w > 0 ? +(acute / chronic4w).toFixed(2) : 1.0;
};

const calcPMC = (activities, days = 90) => {
  const kCTL = Math.exp(-1 / 42), kATL = Math.exp(-1 / 7);
  const dCTL = 1 - kCTL, dATL = 1 - kATL;
  const trimpByDay = {};
  activities.forEach(r => {
    const t = r.trimp || _calcTrimp(r.duree, r.fc_moy);
    trimpByDay[r.date] = (trimpByDay[r.date] || 0) + t;
  });
  const today = new Date();
  let ctl = 0, atl = 0;
  const series = [];
  for (let i = days; i >= 0; i--) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const key = d.toISOString().split("T")[0];
    const trimp = trimpByDay[key] || 0;
    ctl = ctl * kCTL + trimp * dCTL;
    atl = atl * kATL + trimp * dATL;
    series.push({ date: key, ctl: +ctl.toFixed(1), atl: +atl.toFixed(1), tsb: +(ctl - atl).toFixed(1) });
  }
  return series;
};

/* ═══════════════════════════════════════════════════════════════════════════
   TrainingPlanView — Affichage du plan de préparation généré
═══════════════════════════════════════════════════════════════════════════ */
function TrainingPlanView({ profile, runs, races }) {
  const [openWeeks, setOpenWeeks] = useState({});
  const toggleWeek = (n) => setOpenWeeks(s => ({ ...s, [n]: !s[n] }));

  const TYPE_COLORS = {
    fondamentale: T.green,
    seuil:        T.orange,
    vma:          T.red,
    sortie_longue:T.navy,
    récup:        T.blue,
    côtes:        T.purple,
  };
  const TYPE_ICONS = {
    fondamentale: "🏃",
    seuil:        "⚡",
    vma:          "🔥",
    sortie_longue:"🗻",
    récup:        "💤",
    côtes:        "⛰️",
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcoming = (races || []).filter(r => {
    if (!r.date || r.statut === "terminé") return false;
    const [y, m, d] = r.date.split("-").map(Number);
    return new Date(y, m - 1, d) >= today;
  }).sort((a, b) => a.date > b.date ? 1 : -1);

  if (upcoming.length === 0) return (
    <Card>
      <div style={{ textAlign: "center", padding: "32px 16px" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🏁</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 8 }}>Aucune course planifiée</div>
        <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>
          Ajoute tes prochaines courses dans l'onglet <strong>Planning</strong> pour générer ton plan d'entraînement.
        </div>
      </div>
    </Card>
  );

  let plan = null;
  let planError = null;
  try { plan = generateTrainingPlan(profile, runs, upcoming); }
  catch (e) { planError = e?.message || String(e); }

  const statColor = { inscrit: T.blue, cible: T.orange, planifie: T.textMid, terminé: T.green };
  const pmcLast = (() => {
    const p = calcPMC((runs || []).filter(r => r.distance > 0 && r.duree > 0), 84);
    return p.length ? p[p.length - 1] : null;
  })();

  return (
    <>
      {/* Races overview */}
      <Card>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.textMid, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Courses intégrées au plan</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {upcoming.map(r => {
            const [y, m, d] = r.date.split("-").map(Number);
            const wLeft = Math.round((new Date(y, m - 1, d) - today) / (7 * 24 * 3600 * 1000));
            const isTrailR = !!r.isTrail;
            return (
              <div key={r.id} style={{ padding: "10px 16px", borderRadius: 8, border: `1.5px solid ${isTrailR ? T.green : T.navy}30`, background: isTrailR ? T.greenLight : T.blueLight }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
                  {isTrailR ? "🏔️" : "🏁"} {r.nom}
                </div>
                <div style={{ fontSize: 11, color: T.textMid, marginTop: 2 }}>
                  {r.distance}km · J-{wLeft * 7} · <span style={{ color: statColor[r.statut] || T.textMid, fontWeight: 600 }}>{r.statut}</span>
                  {r.objectif ? ` · 🎯 ${r.objectif}` : ""}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
          {[
            { l: "Semaines totales", v: plan ? `${plan.totalWeeks} sem.` : "—" },
            { l: "CTL actuel",       v: pmcLast ? `${pmcLast.ctl.toFixed(0)} pts` : "—" },
            { l: "VMA",              v: profile.vma ? `${profile.vma} km/h` : "—" },
            { l: "Course A",         v: upcoming[upcoming.length - 1]?.nom || "—" },
          ].map(s => (
            <div key={s.l}>
              <div style={{ fontSize: 10, color: T.textMid, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.l}</div>
              <div style={{ fontFamily: T.fontData, fontVariantNumeric: "tabular-nums", fontSize: 15, fontWeight: 700, color: T.text }}>{s.v}</div>
            </div>
          ))}
        </div>
      </Card>

      {planError && <Card><div style={{ padding: 12, fontSize: 12, color: T.red }}>Erreur: {planError}</div></Card>}

      {plan && <>
        {/* Phase legend */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PHASE_NAMES.map((ph, i) => {
            const count = plan.weeks.filter(w => w.phaseIdx === i).length;
            if (!count) return null;
            return (
              <div key={ph} style={{ padding: "4px 12px", borderRadius: 20, background: PHASE_BG[i], border: `1px solid ${PHASE_COLORS[i]}33`, fontSize: 11, fontWeight: 600, color: PHASE_COLORS[i] }}>
                {ph} · {count} sem.
              </div>
            );
          })}
          <div style={{ padding: "4px 12px", borderRadius: 20, background: T.goldLight, border: `1px solid ${T.gold}33`, fontSize: 11, fontWeight: 600, color: T.gold }}>
            Course · {plan.weeks.filter(w => w.isRaceWeek).length} sem.
          </div>
        </div>

        {/* Weeks */}
        {plan.weeks.map(week => {
          const isOpen  = openWeeks[week.weekNum] ?? (week.weekNum === 1);
          const phColor = week.isRaceWeek ? T.gold : PHASE_COLORS[week.phaseIdx] || T.navy;
          const phBg    = week.isRaceWeek ? T.goldLight : PHASE_BG[week.phaseIdx] || T.bg;
          return (
            <div key={week.weekNum} style={{ background: T.surface, borderRadius: 12, border: `0.5px solid ${week.isRaceWeek ? T.gold : T.border}`, boxShadow: T.shadow, overflow: "hidden" }}>
              <div onClick={() => toggleWeek(week.weekNum)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", cursor: "pointer", background: isOpen ? phBg : "transparent", transition: "background 0.15s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: phColor, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: T.fontData }}>
                    {week.isRaceWeek ? "🏁" : `S${week.weekNum}`}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
                      Semaine {week.weekNum} — {week.phase}
                      {week.isRecovery && <span style={{ fontSize: 10, background: T.blueLight, color: T.blue, padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>Récup</span>}
                      {week.isB2B && <span style={{ fontSize: 10, background: T.greenLight, color: T.green, padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>B2B</span>}
                      {week.raceWeek && <span style={{ fontSize: 10, background: T.goldLight, color: T.gold, padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>🏁 {week.raceWeek.nom}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMid }}>{week.weekLabel}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: T.fontData, fontVariantNumeric: "tabular-nums", fontSize: 16, fontWeight: 700, color: phColor }}>{week.volumeKm} km</div>
                    {week.dplus > 0 && <div style={{ fontSize: 10, color: T.textMid }}>D+ {week.dplus}m</div>}
                  </div>
                  <div style={{ fontSize: 12, color: T.textLight, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▼</div>
                </div>
              </div>
              {isOpen && (
                <div style={{ padding: "0 20px 16px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                    {week.sessions.map((s, si) => (
                      <div key={si} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", background: s.isRaceDay ? T.goldLight : T.bg, borderRadius: 8, border: `0.5px solid ${s.isRaceDay ? T.gold : T.border}` }}>
                        <div style={{ minWidth: 28, paddingTop: 1, fontSize: 16 }}>{s.isRaceDay ? "🏅" : TYPE_ICONS[s.type] || "🏃"}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: T.textMid, textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.day}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: s.isRaceDay ? T.gold : (TYPE_COLORS[s.type] || T.navy), background: `${s.isRaceDay ? T.gold : (TYPE_COLORS[s.type] || T.navy)}18`, padding: "1px 7px", borderRadius: 10 }}>
                              {s.isRaceDay ? "course" : s.type}
                            </span>
                            <span style={{ fontFamily: T.fontData, fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 700, color: T.text, marginLeft: "auto" }}>{s.km} km</span>
                          </div>
                          <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.5 }}>{s.description}</div>
                          {/* Structure détaillée */}
                          {s.structure && s.structure.length > 0 && (
                            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                              {s.structure.map((bloc, bi) => (
                                <div key={bi} style={{ display: "flex", gap: 8, padding: "6px 10px", background: T.surface, borderRadius: 6, border: `0.5px solid ${T.border}`, alignItems: "center" }}>
                                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: `${TYPE_COLORS[s.type] || T.navy}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: TYPE_COLORS[s.type] || T.navy, flexShrink: 0 }}>{bi + 1}</div>
                                  <div>
                                    <span style={{ fontWeight: 700, fontSize: 11, color: T.text }}>{bloc.label}</span>
                                    {bloc.detail && <span style={{ fontSize: 11, color: T.textMid, fontFamily: T.fontData, marginLeft: 6 }}>{bloc.detail}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* Week total */}
                    <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4, borderTop: `1px solid ${T.border}` }}>
                      <span style={{ fontFamily: T.fontData, fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 700, color: T.textMid }}>
                        Total : {week.sessions.reduce((a, s) => a + s.km, 0)} km
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </>}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SessionCatalogueView — Catalogue de séances personnalisées
═══════════════════════════════════════════════════════════════════════════ */
function SessionCatalogueView({ profile, runs, sleep, addRun }) {
  const vma     = profile.vma      || 15;
  const fcMax   = profile.fc_max   || 192;
  const fcRepos = profile.fc_repos || 52;
  const [selected, setSelected] = useState(null);
  const [addedId,  setAddedId]  = useState(null);

  // ── Helpers allure ────────────────────────────────────────────────────────
  const p = (kmh) => {
    const secKm = 3600 / kmh;
    return `${Math.floor(secKm / 60)}:${String(Math.round(secKm % 60)).padStart(2, "0")}`;
  };
  const fcZ = (pct) => Math.round(fcRepos + (fcMax - fcRepos) * pct);

  // ── Catalogue de séances ──────────────────────────────────────────────────
  const seances = [
    {
      id: "recup",
      type: "recup",
      icon: "🌿",
      label: "Récupération active",
      couleur: T.green,
      dureeMin: 35,
      distance: +(vma * 0.60 * 35 / 60).toFixed(1),
      fc_moy: fcZ(0.58),
      description: "Footing très léger, jambes libres, respiration calme.",
      structure: [
        { label: "Footing Z1", detail: `35 min à ${p(vma * 0.60)} /km — FC cible ${fcZ(0.55)}–${fcZ(0.62)} bpm` },
      ],
      conseils: "Idéale lendemain de compétition ou séance intensive. Ne regardez pas votre allure, pilotez uniquement à la FC.",
      ressenti: 2,
    },
    {
      id: "fond",
      type: "fond",
      icon: "🏃",
      label: "Endurance fondamentale",
      couleur: T.blue,
      dureeMin: 55,
      distance: +(vma * 0.68 * 55 / 60).toFixed(1),
      fc_moy: fcZ(0.67),
      description: "La séance de base. Développe le moteur aérobie.",
      structure: [
        { label: "Échauffement",    detail: `10 min à ${p(vma * 0.62)} /km` },
        { label: "Corps de séance", detail: `40 min à ${p(vma * 0.68)} /km — FC ${fcZ(0.64)}–${fcZ(0.72)} bpm` },
        { label: "Retour au calme", detail: `5 min très lent` },
      ],
      conseils: "Vous devez pouvoir tenir une conversation. Si vous haletez, ralentissez. Constituez 70–80% de votre volume hebdomadaire avec ces sorties.",
      ressenti: 2,
    },
    {
      id: "longrun",
      type: "longrun",
      icon: "🛤️",
      label: "Sortie longue",
      couleur: T.purple,
      dureeMin: 90,
      distance: +(vma * 0.65 * 90 / 60).toFixed(1),
      fc_moy: fcZ(0.65),
      description: "Séance clé de la semaine. Endurance musculaire et mentale.",
      structure: [
        { label: "Départ lent",     detail: `20 min à ${p(vma * 0.62)} /km` },
        { label: "Milieu",          detail: `50 min à ${p(vma * 0.65)} /km — FC ${fcZ(0.62)}–${fcZ(0.70)} bpm` },
        { label: "Progression fin", detail: `20 min en remontant vers ${p(vma * 0.70)} /km` },
      ],
      conseils: "Emportez ravitaillement si >75 min (gel ou barre). Ne partez pas trop vite — les 20 dernières minutes sont les plus importantes.",
      ressenti: 3,
    },
    {
      id: "tempo",
      type: "tempo",
      icon: "🔥",
      label: "Tempo / Allure marathon",
      couleur: T.orange,
      dureeMin: 50,
      distance: +(vma * 0.80 * 30 / 60 + vma * 0.68 * 20 / 60).toFixed(1),
      fc_moy: fcZ(0.78),
      description: "Effort soutenu au rythme marathon. Développe le seuil lactique.",
      structure: [
        { label: "Échauffement",    detail: `15 min à ${p(vma * 0.65)} /km` },
        { label: "Bloc tempo",      detail: `20–25 min à ${p(vma * 0.80)} /km — FC ${fcZ(0.75)}–${fcZ(0.82)} bpm` },
        { label: "Retour au calme", detail: `10 min léger` },
      ],
      conseils: "Cette allure doit être inconfortable mais tenable. Si vous ne pouvez plus dire quelques mots, c'est trop vite.",
      ressenti: 3,
    },
    {
      id: "seuil",
      type: "tempo",
      icon: "⚡",
      label: "Seuil lactique",
      couleur: "#CA8A04",
      dureeMin: 55,
      distance: +(vma * 0.87 * 20 / 60 + vma * 0.68 * 20 / 60).toFixed(1),
      fc_moy: fcZ(0.85),
      description: "Intervalles au seuil. Repousse la limite de l'endurance.",
      structure: [
        { label: "Échauffement",    detail: `15 min progressif` },
        { label: "3 × 8 min seuil", detail: `à ${p(vma * 0.87)} /km — FC ${fcZ(0.82)}–${fcZ(0.88)} bpm — récup 3 min entre` },
        { label: "Retour au calme", detail: `10 min très lent` },
      ],
      conseils: "Chaque répétition doit se faire à la même allure. Si vous accélérez sur la dernière, vous êtes parti trop lentement — c'est bien !",
      ressenti: 4,
    },
    {
      id: "fractionne_court",
      type: "fractionne",
      icon: "🏟️",
      label: "Fractionné court — VMA",
      couleur: T.red,
      dureeMin: 50,
      distance: +(vma * 1.00 * 16 / 60 + vma * 0.65 * 20 / 60).toFixed(1),
      fc_moy: fcZ(0.88),
      description: "Développe la VMA et la capacité à tenir l'effort maximal.",
      structure: [
        { label: "Échauffement",    detail: `15 min + 4 accélérations 20s` },
        { label: "8 × 400m",        detail: `à ${p(vma * 1.00)} /km (100% VMA) — récup 1:30 trot entre` },
        { label: "Retour au calme", detail: `10 min lent` },
      ],
      conseils: "La récupération active est importante — trottez, ne marchez pas. Si vous ne tenez pas l'allure à la 6e répétition, stoppez les intervalles.",
      ressenti: 4,
    },
    {
      id: "fractionne_long",
      type: "fractionne",
      icon: "📏",
      label: "Fractionné long — VO2max",
      couleur: "#DC2626",
      dureeMin: 60,
      distance: +(vma * 0.95 * 15 / 60 + vma * 0.65 * 25 / 60).toFixed(1),
      fc_moy: fcZ(0.90),
      description: "Intervalles longs pour développer VO2max et résistance.",
      structure: [
        { label: "Échauffement",    detail: `20 min progressif` },
        { label: "5 × 1000m",       detail: `à ${p(vma * 0.95)} /km — FC cible ${fcZ(0.88)}–${fcZ(0.93)} bpm — récup 2 min` },
        { label: "Retour au calme", detail: `10 min très lent` },
      ],
      conseils: "Commencez les 1000m prudemment. L'objectif est que votre FC atteigne la cible à la fin de chaque répétition, pas au début.",
      ressenti: 5,
    },
    {
      id: "cotes",
      type: "fractionne",
      icon: "⛰️",
      label: "Côtes / Hill repeats",
      couleur: T.textMid,
      dureeMin: 45,
      distance: +(vma * 0.70 * 30 / 60).toFixed(1),
      fc_moy: fcZ(0.82),
      description: "Renforce les jambes et améliore la puissance sans impact.",
      structure: [
        { label: "Échauffement",    detail: `10 min plat à ${p(vma * 0.65)} /km` },
        { label: "8–10 × côte",     detail: `30–45s en côte à effort maximal — FC ${fcZ(0.88)}–${fcZ(0.94)} bpm — descente trot` },
        { label: "Retour au calme", detail: `10 min plat léger` },
      ],
      conseils: "Choisissez une côte de 4–6% sur 150–200m. Montez en appui sur l'avant du pied, bras actifs. La descente compte comme récupération.",
      ressenti: 4,
    },
  ];

  const handleAdd = (s) => {
    if (!addRun) return;
    addRun({
      type: s.type,
      date: new Date().toISOString().split("T")[0],
      distance: s.distance,
      duree: s.dureeMin,
      fc_moy: s.fc_moy,
      fc_max: 0,
      denivele: 0,
      cadence: 0,
      calories: Math.round(s.dureeMin * 9.5),
      temp: null,
      shoes_id: null,
      ressenti: s.ressenti,
      note: s.label,
      surface: "route",
    });
    setAddedId(s.id);
    setTimeout(() => setAddedId(null), 2500);
  };

  const acwr  = calcACWR(runs);
  const score = (() => { const sl = (sleep || [])[sleep.length - 1]; return sl ? sl.qualite : 70; })();
  const reco  = acwr > 1.4 || score < 55
    ? { label: "Récupération recommandée", ids: ["recup"], msg: `ACWR ${acwr} ou sommeil ${score}% — votre corps demande du repos.` }
    : acwr < 0.8
    ? { label: "Charge à augmenter", ids: ["fond", "longrun"], msg: "Volume faible cette semaine — bonne fenêtre pour une sortie longue." }
    : { label: "Forme optimale", ids: ["tempo", "fractionne_court", "seuil"], msg: "Votre charge est équilibrée — jour idéal pour une séance de qualité." };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Bannière du jour */}
      <Card style={{ background: selected ? T.surface : `${T.navy}06`, borderColor: T.navy + "22" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Recommandation du jour</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 4 }}>{reco.label}</div>
            <div style={{ fontSize: 13, color: T.textMid }}>{reco.msg}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {reco.ids.map(id => {
              const s = seances.find(x => x.id === id);
              if (!s) return null;
              return (
                <div key={id} onClick={() => setSelected(s)} style={{
                  padding: "8px 16px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  background: selected?.id === id ? T.navy : T.surface,
                  color: selected?.id === id ? "#fff" : T.text,
                  border: `1px solid ${selected?.id === id ? T.navy : T.border}`,
                  fontWeight: 600, fontSize: 12, transition: "all 0.12s",
                }}>
                  {s.icon} {s.label}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Catalogue complet */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {seances.map(s => (
          <Card key={s.id} onClick={() => setSelected(selected?.id === s.id ? null : s)} style={{
            cursor: "pointer",
            border: `1px solid ${selected?.id === s.id ? s.couleur : T.border}`,
            background: selected?.id === s.id ? `${s.couleur}08` : T.surface,
            transition: "all 0.12s",
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: `${s.couleur}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{s.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{s.dureeMin} min · {s.distance} km · FC {s.fc_moy} bpm</div>
                </div>
              </div>
              <div style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: `${s.couleur}15`, color: s.couleur, fontWeight: 700, flexShrink: 0 }}>
                {["", "😊", "💪", "🔥", "⚡", "💥"][s.ressenti]}
              </div>
            </div>
            <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.55 }}>{s.description}</div>
          </Card>
        ))}
      </div>

      {/* Détail séance sélectionnée */}
      {selected && (
        <Card style={{ border: `2px solid ${selected.couleur}44` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${selected.couleur}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{selected.icon}</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, color: T.text }}>{selected.label}</div>
                <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>Allures calculées pour VMA {vma} km/h · FC max {fcMax} bpm</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, fontSize: 18 }}>✕</button>
            </div>
          </div>

          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
            {[
              { l: "Durée",      v: `${selected.dureeMin} min`,     c: "#111" },
              { l: "Distance",   v: `~${selected.distance} km`,      c: T.blue },
              { l: "FC moyenne", v: `${selected.fc_moy} bpm`,        c: T.orange },
              { l: "Intensité",  v: ["", "Légère", "Modérée", "Soutenue", "Intensive", "Maximale"][selected.ressenti], c: selected.couleur },
            ].map((k, i) => (
              <div key={i} style={{ background: T.surfaceSub, borderRadius: 6, padding: "10px 14px", border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{k.l}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: k.c, fontFamily: T.fontData, fontVariantNumeric: "tabular-nums" }}>{k.v}</div>
              </div>
            ))}
          </div>

          {/* Structure */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Structure</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {selected.structure.map((bloc, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "10px 14px", background: T.surfaceSub, borderRadius: 6, border: `1px solid ${T.border}`, alignItems: "center" }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: `${selected.couleur}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: selected.couleur, flexShrink: 0 }}>{i + 1}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: T.text, marginBottom: 2 }}>{bloc.label}</div>
                    <div style={{ fontSize: 12, color: T.textMid, fontFamily: T.fontData, fontVariantNumeric: "tabular-nums" }}>{bloc.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conseils */}
          <div style={{ padding: "12px 14px", background: `${selected.couleur}08`, borderRadius: 6, border: `1px solid ${selected.couleur}25`, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: selected.couleur, marginBottom: 4 }}>Conseil coach</div>
            <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.65 }}>{selected.conseils}</div>
          </div>

          {/* Bouton enregistrer */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {addedId === selected.id ? (
              <div style={{ padding: "10px 20px", borderRadius: 6, background: "#F0FDF4", border: "1px solid #16A34A44", color: T.green, fontWeight: 700, fontSize: 13 }}>
                Séance ajoutée au journal !
              </div>
            ) : (
              <Btn variant="lime" onClick={() => handleAdd(selected)}>
                + Enregistrer cette séance
              </Btn>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CoachPage — Composant principal exporté
═══════════════════════════════════════════════════════════════════════════ */
export default function CoachPage({ profile, runs, sleep, races, addRun }) {
  const [activeView, setActiveView] = useState("plan");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 8 }}>
        {[
          { id: "plan",    label: "Mon plan de prépa" },
          { id: "seances", label: "Séances" },
        ].map(v => (
          <button key={v.id} onClick={() => setActiveView(v.id)} style={{
            padding: "10px 20px", borderRadius: 8, fontWeight: 700, fontSize: 13,
            cursor: "pointer", border: "none", fontFamily: "'Bricolage Grotesque', sans-serif",
            background: activeView === v.id ? T.navy : T.surface,
            color: activeView === v.id ? "#fff" : T.textMid,
            boxShadow: activeView === v.id ? "0 2px 8px rgba(0,0,0,0.12)" : "none",
            transition: "all 0.15s",
          }}>
            {v.label}
          </button>
        ))}
      </div>

      {activeView === "plan"    && <TrainingPlanView profile={profile} runs={runs} races={races} />}
      {activeView === "seances" && <SessionCatalogueView profile={profile} runs={runs} sleep={sleep} addRun={addRun} />}
    </div>
  );
}
